// RFC 9839 client-side validator. Mirrors `Doi.UnicodeAssignables`.
// Rejects Cs / Co / Cn / Cc except TAB, LF, CR.

const ALLOWED_CONTROLS = new Set<number>([0x0009, 0x000a, 0x000d]);

const DISALLOWED_CATEGORY_RE = /^(?:\p{Cs}|\p{Co}|\p{Cn}|\p{Cc})$/u;
const CC_RE = /^\p{Cc}$/u;

export interface AssignableResult {
  ok: boolean;
  /** The first offending codepoint, if any. */
  codepoint?: number;
}

export function validateAssignables(text: string): AssignableResult {
  for (const ch of text) {
    if (!DISALLOWED_CATEGORY_RE.test(ch)) continue;
    const cp = ch.codePointAt(0) ?? 0;
    // Allow TAB/LF/CR inside the Cc category.
    if (CC_RE.test(ch) && ALLOWED_CONTROLS.has(cp)) continue;
    return { ok: false, codepoint: cp };
  }
  return { ok: true };
}

export function isAssignable(text: string): boolean {
  return validateAssignables(text).ok
}

/** Format a codepoint as `U+XXXX` with minimum 4 hex digits. */
export function formatCodepoint(cp: number): string {
  return "U+" + cp.toString(16).toUpperCase().padStart(4, "0");
}

/** Count codepoints in a string (not UTF-16 code units). */
export function codepointLength(text: string): number {
  let n = 0;
  for (const _ of text) n++;
  return n;
}

export interface NameValidation {
  ok: boolean;
  error?: string;
}

/** Validate a deck name: 1–50 codepoints, not all-whitespace, assignable. */
export function validateDeckName(raw: string): NameValidation {
  if (raw.length === 0) return { ok: false, error: "Name is required" };
  if (raw.trim().length === 0) return { ok: false, error: "Name can't be blank" };
  const len = codepointLength(raw);
  if (len > 50) return { ok: false, error: `Name is too long (max 50 characters; got ${len})` };
  const a = validateAssignables(raw);
  if (!a.ok) {
    return {
      ok: false,
      error: `Name contains a disallowed character (${formatCodepoint(a.codepoint!)})`,
    };
  }
  return { ok: true };
}

/** Validate a username: 3–32 codepoints, no whitespace, assignable. */
export function validateUsername(raw: string): NameValidation {
  const len = codepointLength(raw);
  if (len < 3) return { ok: false, error: "Username must be at least 3 characters" };
  if (len > 32) return { ok: false, error: "Username is too long (max 32)" };
  if (/\s/u.test(raw)) return { ok: false, error: "Username must not contain whitespace" };
  const a = validateAssignables(raw);
  if (!a.ok) {
    return {
      ok: false,
      error: `Username contains a disallowed character (${formatCodepoint(a.codepoint!)})`,
    };
  }
  return { ok: true };
}
